import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { Message, MessageSeverity } from 'constants/message'
import { HTTP_CODES } from 'constants/constants'
import './widgetProgressByType.css'
import ProgressionForTypeTask from './components/progressionForTypeTask'
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import MessageContext from 'context/MessageContext'
import LoadingContext from 'context/LoadingContext'
import UrlApis from 'utils/api/urlApis'
import AuthContext from 'context/AuthContext'

const WidgetProgressByType = ({ bdvnumdoc }) => {
  const { t } = useTranslation()

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)
  const [maxOfAllTotals, setMaxOfAllTotals] = useState(0)

  const { lang } = useContext(LangContext)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress } = useContext(LoadingContext)
  const { handleRequestLogin } = useContext(AuthContext)

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    try {
      console.debug(
        'Widget <' +
          t('9500012') +
          '> :: called URL = ' +
          encodeURI(UrlApis.URL_WPSTATUS_PROGRESS_BY_TYPE_GET, +'?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
      )
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(UrlApis.URL_WPSTATUS_PROGRESS_BY_TYPE_GET + '?lang=' + lang + '&bdvnumdoc=' + bdvnumdoc),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Init widget locally
      console.debug('data retrieved <' + t('9500012') + '>')
      const dataReturned = response?.data
      setData(dataReturned)
      // Compute max of all totals for aligning the display of sub-progress-bars
      setMaxOfAllTotals(
        dataReturned
          ? Math.max(
              dataReturned.opTotal,
              dataReturned.oopTotal,
              dataReturned.oeTotal,
              dataReturned.oeTLTotal,
              dataReturned.oeMCTotal,
              dataReturned.oeDOCTotal,
              dataReturned.oeKXTotal,
              dataReturned.oeMISTotal,
              dataReturned.tsTotal,
            )
          : 0,
      )
    } catch (err) {
      console.debug('ERR <' + t('9500012') + '>: ' + err)
      if (err.response.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500012'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500012'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500012'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      handleLoadInProgress(false)
      setIsLoadInProgressActive(false)
    }
  }

  // Sync on receiving data from server (axios)
  useEffect(() => {
    if (bdvnumdoc !== undefined) {
      handleLoadInProgress(true)
      setIsLoadInProgressActive(true)
      loadData()
    }
  }, [bdvnumdoc])

  return (
    <div className='WidgetWpProgressByType'>
      <Typography variant='large' style={{ marginBottom: '20px' }}>
        {t('9500012')}
      </Typography>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' && maxOfAllTotals !== 0 ? (
        <div>
          <Table>
            <colgroup>
              <col style={{ width: '14%' }} />
              <col />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell colSpan='2' className='description'>
                  {t('9500107')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className='emphasized' style={{ width: '10%' }}>
                  {t('9500097')}
                </TableCell>
                <TableCell className='emphasized'>
                  <ProgressionForTypeTask
                    tasksCompleted={data.opDone}
                    tasksDeferred={data.opReported}
                    tasksTotal={data.opTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500119')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.noopDone}
                    tasksDeferred={data.noopReported}
                    tasksTotal={data.noopTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500098')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oopDone}
                    tasksDeferred={data.oopReported}
                    tasksTotal={data.oopTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='emphasized'>{t('9500099')}</TableCell>
                <TableCell className='emphasized'>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeDone}
                    tasksDeferred={data.oeReported}
                    tasksTotal={data.oeTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500100')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeTLDone}
                    tasksDeferred={data.oeTLReported}
                    tasksTotal={data.oeTLTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500101')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeMCDone}
                    tasksDeferred={data.oeMCReported}
                    tasksTotal={data.oeMCTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500102')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeDOCDone}
                    tasksDeferred={data.oeDOCReported}
                    tasksTotal={data.oeDOCTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500103')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeKXDone}
                    tasksDeferred={data.oeKXReported}
                    tasksTotal={data.oeKXTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <dd>{t('9500104')}</dd>
                </TableCell>
                <TableCell>
                  <ProgressionForTypeTask
                    tasksCompleted={data.oeMISDone}
                    tasksDeferred={data.oeMISReported}
                    tasksTotal={data.oeMISTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='emphasized'>{t('9500105')}</TableCell>
                <TableCell className='emphasized'>
                  <ProgressionForTypeTask
                    tasksCompleted={data.tsDone}
                    tasksDeferred={data.tsReported}
                    tasksTotal={data.tsTotal}
                    maxOfAllTotals={maxOfAllTotals}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <div className='progressByTypelegend'>
            <div className='tasksCompleted'>{t('9500021')}</div>
            <div className='tasksToDo'>{t('9500022')}</div>
            <div className='tasksDeferred'>{t('9500096')}</div>
          </div>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetProgressByType
